import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { api, errorMessage } from '../api/client'
import { MeResponseSchema } from '@shared/schema/api'
import { useSession } from './SessionProvider'
import { Button, Field, TextInput, Alert, LoadingScreen } from '../ui'

function BrandHeader({ subtitle }: { subtitle: string }) {
  return (
    <div className="text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--fs-primary)] text-2xl font-bold text-white">
        MT
      </div>
      <h1 className="mt-4 text-xl font-semibold text-slate-900">Flow Studio</h1>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
    </div>
  )
}

export function LoginPage() {
  const { state, setSession } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (state.status === 'loading') return <LoadingScreen />
  if (state.status === 'setupRequired') return <Navigate to="/setup" replace />
  if (state.status === 'authenticated') return <Navigate to="/" replace />

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const response = await api.post('/api/auth/login', { email, password }, MeResponseSchema)
      setSession(response)
      const from = (location.state as { from?: string } | null)?.from
      navigate(from && from !== '/login' ? from : '/', { replace: true })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-5 rounded-2xl bg-white p-7 shadow-sm ring-1 ring-slate-200"
      >
        <BrandHeader subtitle="เข้าสู่ระบบเพื่อใช้งาน" />

        {error && <Alert variant="error">{error}</Alert>}

        <Field label="อีเมล" htmlFor="email" required>
          <TextInput
            id="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="รหัสผ่าน" htmlFor="password" required>
          <TextInput
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          เข้าสู่ระบบ
        </Button>
      </form>
    </div>
  )
}

export function FirstRunSetupPage() {
  const { state, setSession } = useSession()
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (state.status === 'loading') return <LoadingScreen />
  if (state.status !== 'setupRequired') return <Navigate to="/" replace />

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    if (form.password !== form.confirm) {
      setError('รหัสผ่านทั้งสองช่องไม่ตรงกัน')
      return
    }
    setBusy(true)
    try {
      const response = await api.post(
        '/api/auth/setup',
        { email: form.email, name: form.name, password: form.password },
        MeResponseSchema,
      )
      setSession(response)
      navigate('/', { replace: true })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const update = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-5 rounded-2xl bg-white p-7 shadow-sm ring-1 ring-slate-200"
      >
        <BrandHeader subtitle="ตั้งค่าครั้งแรก — สร้างบัญชีผู้ดูแลระบบ" />

        {error && <Alert variant="error">{error}</Alert>}

        <Field label="ชื่อ-นามสกุล" htmlFor="name" required>
          <TextInput id="name" required value={form.name} onChange={update('name')} />
        </Field>

        <Field label="อีเมล" htmlFor="setup-email" required>
          <TextInput
            id="setup-email"
            type="email"
            inputMode="email"
            autoComplete="username"
            required
            value={form.email}
            onChange={update('email')}
          />
        </Field>

        <Field label="รหัสผ่าน" htmlFor="setup-password" hint="อย่างน้อย 8 ตัวอักษร" required>
          <TextInput
            id="setup-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={form.password}
            onChange={update('password')}
          />
        </Field>

        <Field label="ยืนยันรหัสผ่าน" htmlFor="setup-confirm" required>
          <TextInput
            id="setup-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={form.confirm}
            onChange={update('confirm')}
          />
        </Field>

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          สร้างบัญชีผู้ดูแล
        </Button>
      </form>
    </div>
  )
}
